import { useState, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useFilters } from "../context/FiltersContext";
import { AuthContext } from "../context/AuthContext";
import { ProsContext } from "../context/ProsContext";
import { useFirstname } from "../hooks/useFirstname";
import { getItem } from "../api/jobExplorer";
import PremiumModal from "../components/premium/PremiumModal";
import { JOB_CONFIG } from "../config/job";
import { useAlgoliaChat } from "../hooks/useAlgoliaChat";
import { SearchBar, SummaryBar, FilterPanel } from "../components/search";
import { JobSuggestions, JobCard, DetailPanel } from "../components/job";
import { isProAccessBlocked } from "../config/freemium";

export default function Job({ onSend, initActivity }) {
  const { user, updateUser } = useContext(AuthContext);
  const { itemsPro, setItemsPro } = useContext(ProsContext);
  const { getRandomFirstname } = useFirstname();
  const navigate = useNavigate();
  const { filters: filtersData } = useFilters();
  const cfg = JOB_CONFIG;
  const [videoDone, setVideoDone] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const videoRef = useRef(null);

  function handlePlayVideo() {
    setVideoPlaying(true);
    videoRef.current?.play();
  }
  const [actionLoading, setActionLoading] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [premiumModalReason, setPremiumModalReason] = useState('');

  const {
    inputValue, setInputValue,
    results, facets, hasSearched, thinking, filterLoading, loadingMore,
    summary, filterState, nbHits, showFilters, setShowFilters,
    selectedHit, setSelectedHit, recording, transcribing, activeCount,
    inputRef, sentinelRef,
    handleSubmit, handleReset, handleFilterToggle, handleFilterReset,
    toggleRecording, runSearch,
  } = useAlgoliaChat({
    algoliaIndex: cfg.algoliaIndex,
    assistantName: 'job',
    filterDimensions: cfg.filterDimensions,
    filtersData,
  });

  function isPremiumBlocked(jobId) {
    return isProAccessBlocked(user, itemsPro, jobId);
  }

  function getOrCreatePro(job, gender) {
    if (itemsPro[job.id]) {
      return { name: itemsPro[job.id].name, gender: itemsPro[job.id].gender };
    }
    const name = gender === 'boy' ? getRandomFirstname('male') : getRandomFirstname('female');
    setItemsPro({ ciaov2_job_id: job.id, name, gender, title: gender === 'boy' ? job.title_m : job.title_f });
    return { name, gender };
  }

  async function handleClickChat() {
    const hit = selectedHit;
    if (isPremiumBlocked(hit.objectID)) {
      setPremiumModalReason(`échanger avec un professionnel`);
      setShowPremiumModal(true);
      return;
    }
    setActionLoading(true);
    try {
      const job = await getItem(hit.objectID);
      const gender = user.gender;
      const { name } = getOrCreatePro(job, gender);
      const qualities = job._qualities.map(q => `${q._quality.label} : ${q.explanation}`).join("\n");
      const level_of_education = job._study_levels.map(s => s._study_level.label).join(", ");
      const salary = `Salaire début de carrière de ${job.salary_min}€ pouvant aller jusqu'à ${job.salary_max}€. ${job.salary_explanation}`;
      const salary_evolution_explanation = job.salary_evolutions.map(e =>
        `${e.experience} (${e.experience_years}) gagne environ ${e.experience_salary}. Role : ${e.experience_role}.`
      ).join("\n");
      const values = {
        id: job.id,
        title: gender === 'boy' ? job.title_m : job.title_f,
        title_for_prompt: gender === 'boy' ? job.title_for_prompt_m : job.title_for_prompt_f,
        name,
        picture: gender === 'boy' ? job.aws_pictures_m[1] : job.aws_pictures_f[1],
        qualities,
        salary,
        salary_evolution_explanation,
        level_of_education,
        gender,
        from: 'job',
      };
      const path = await onSend(values);
      navigate(path);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleClickQuizz() {
    const hit = selectedHit;
    if (isPremiumBlocked(hit.objectID)) {
      const startsWithVowel = /^[aeiouàâäéèêëïîôùûü]/i.test(hit.title);
      setPremiumModalReason(`se tester dans le métier ${startsWithVowel ? "d'" : "de "}${hit.title}`);
      setShowPremiumModal(true);
      return;
    }
    setActionLoading(true);
    try {
      const job = await getItem(hit.objectID);
      const gender = user.gender;
      getOrCreatePro(job, gender);
      const made_for_you = user.age < 16 ? job.made_for_you_14_v2 : job.made_for_you_16_v2;
      const values = {
        jobId: job.id,
        gender,
        title: gender === 'boy' ? job.title_m : job.title_f,
        photo_url: gender === 'boy' ? job.aws_pictures_m[1] : job.aws_pictures_f[1],
        type: 'quizz',
        made_for_you: made_for_you.map(item => item.text).join("\n"),
        from: 'job',
      };
      const path = await initActivity(values);
      navigate(path);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleClickGritAndFit() {
    const hit = selectedHit;
    if (isPremiumBlocked(hit.objectID)) {
      const startsWithVowel = /^[aeiouàâäéèêëïîôùûü]/i.test(hit.title);
      setPremiumModalReason(`faire une immersion dans le métier ${startsWithVowel ? "d'" : "de "}${hit.title}`);
      setShowPremiumModal(true);
      return;
    }
    setActionLoading(true);
    try {
      const job = await getItem(hit.objectID);
      const gender = user.gender;
      getOrCreatePro(job, gender);
      const made_for_you = user.age < 16 ? job.made_for_you_14_v2 : job.made_for_you_16_v2;
      const values = {
        jobId: job.id,
        gender,
        title: gender === 'boy' ? job.title_m : job.title_f,
        photo_url: gender === 'boy' ? job.aws_pictures_m[1] : job.aws_pictures_f[1],
        type: 'grit-and-fit',
        made_for_you: made_for_you.map(item => item.text).join("\n"),
        from: 'job',
      };
      const path = await initActivity(values);
      navigate(path);
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <div className="flex-1 min-w-0 flex flex-col overflow-hidden bg-gray-900">

      {/* HEADER */}
      <div className="px-5 py-3 border-b border-gray-800 shrink-0 flex items-center gap-3">
        <div className="ml-12 md:ml-0 w-9 h-9 rounded-xl bg-teal-500 flex items-center justify-center text-base font-bold text-white shrink-0">
          {cfg.header.icon}
        </div>
        <div className="min-w-0">
          <h1 className="text-sm font-bold text-white leading-tight">{cfg.header.title}</h1>
          <p className="text-xs text-gray-500">{cfg.header.subtitle}</p>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden relative">

        {/* MAIN */}
        <div className="flex-1 min-w-0 flex flex-col overflow-hidden">

          {/* CONTRÔLES STICKY */}
          <div className="px-5 pt-4 pb-0 shrink-0">
            <SearchBar
              value={inputValue}
              onChange={setInputValue}
              onSubmit={handleSubmit}
              onReset={handleReset}
              hasSearched={hasSearched}
              recording={recording}
              transcribing={transcribing}
              thinking={thinking}
              onToggleRecording={toggleRecording}
              inputRef={inputRef}
              placeholder={hasSearched ? "Affine ta recherche…" : "Décris les métiers que tu recherches…"}
            />
            {!thinking && (
              <SummaryBar
                summary={summary}
                nbHits={nbHits}
                resultLabel={cfg.resultLabel}
                filterLoading={filterLoading}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters((s) => !s)}
                activeCount={activeCount}
              />
            )}
          </div>

          {/* ZONE SCROLLABLE */}
          <div className="flex-1 overflow-y-auto job-scrollbar px-5 pb-6">

            {/* Thinking */}
            {thinking && (
              <div className="flex items-center gap-3 px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl mb-5 mt-4 text-sm text-gray-400">
                <div className="flex gap-1">
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      className="w-1.5 h-1.5 rounded-full bg-teal-500 inline-block animate-bounce"
                      style={{ animationDelay: `${i * 0.15}s` }}
                    />
                  ))}
                </div>
                {cfg.header.title} analyse ta demande…
              </div>
            )}

            {/* Grille de résultats */}
            {hasSearched && !thinking && (
              results.length === 0 && !filterLoading ? (
                <div className="text-center py-16">
                  <div className="text-4xl mb-3">🔍</div>
                  <p className="text-sm font-semibold text-gray-300 mb-1.5">Aucun {cfg.resultLabel.singular} trouvé</p>
                  <p className="text-xs text-gray-500">Essaie de changer les filtres ou de reformuler ta recherche</p>
                </div>
              ) : (
                <>
                  <div
                    className={`grid gap-3.5 pt-4 transition-opacity ${filterLoading ? "opacity-50" : "opacity-100"}`}
                    style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}
                  >
                    {results.map((hit, i) => (
                      <JobCard key={hit.objectID || i} hit={hit} onSelect={setSelectedHit} />
                    ))}
                  </div>
                  <div ref={sentinelRef} className="mt-4 flex justify-center py-4">
                    {loadingMore && (
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <div className="w-4 h-4 rounded-full border-2 border-teal-500/40 border-t-teal-500 animate-spin" />
                        Chargement…
                      </div>
                    )}
                  </div>
                </>
              )
            )}

            {/* État initial */}
            {!hasSearched && !thinking && (
              <div className="pt-8 flex flex-col items-center">
                <div className="text-center mb-6 max-w-md">
                  <p className="text-base font-bold text-white mb-2">Salut, je suis Job 👋</p>
                  <p className="text-sm text-gray-400">
                    Dis-moi ce que tu cherches, un domaine, une passion ou un niveau d'études. Je te trouve les métiers qui correspondent.
                  </p>
                </div>

                {!videoDone ? (
                  <div className="relative rounded-xl overflow-hidden w-full max-w-2xl border border-gray-700 min-h-[200px] md:min-h-[378px]">
                    <video
                      ref={videoRef}
                      src="/assets/job-demo.mp4"
                      playsInline
                      controls
                      className="w-full block"
                      onEnded={() => setVideoDone(true)}
                    />
                    {!videoPlaying && (
                      <div
                        className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer bg-gray-900"
                        onClick={handlePlayVideo}
                      >
                        {/* Glow de fond */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="w-[500px] h-[500px] rounded-full bg-teal-500 opacity-10" style={{ filter: "blur(120px)" }} />
                        </div>

                        {/* Bouton play avec halo */}
                        <div className="relative flex items-center justify-center mb-6 z-10">
                          <div className="absolute w-28 h-28 rounded-full border border-teal-500/30 bg-teal-500/5" />
                          <div className="w-16 h-16 rounded-full bg-teal-500 flex items-center justify-center shadow-[0_0_32px_rgba(20,184,166,0.4)]">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                              <polygon points="6,4 20,12 6,20" />
                            </svg>
                          </div>
                        </div>

                        {/* "Job by ORIA" */}
                        <div className="flex items-baseline gap-3 mb-3 z-10">
                          <span className="text-5xl font-black text-white" style={{ letterSpacing: -2 }}>Job</span>
                          <span className="text-lg font-medium text-teal-400">by ORIA</span>
                        </div>

                        {/* Séparateur */}
                        <div className="h-0.5 w-32 mb-3 z-10" style={{ background: "linear-gradient(90deg, transparent, #14b8a6, #5eead4, transparent)" }} />

                        {/* Sous-titre */}
                        <p className="text-sm text-gray-400 z-10">Ton expert métier, disponible 24h/24.</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <JobSuggestions
                    suggestions={cfg.suggestions}
                    onSelect={(text) => runSearch(text, [])}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* FILTER PANEL */}
        {showFilters && (
          <>
            <div
              className="fixed inset-0 z-40 bg-black/50 md:hidden"
              onClick={() => setShowFilters(false)}
            />
            <div className="fixed top-0 right-0 h-full z-50 bg-gray-900 w-[260px] shadow-[-4px_0_24px_rgba(0,0,0,0.4)] md:relative md:w-[240px] md:border-l md:border-gray-800 md:z-auto md:shadow-none flex flex-col">
              <FilterPanel
                filterState={filterState}
                facets={facets}
                filterDimensions={cfg.filterDimensions}
                filtersData={filtersData}
                onToggle={handleFilterToggle}
                onReset={handleFilterReset}
                activeCount={activeCount}
                onClose={() => setShowFilters(false)}
              />
            </div>
          </>
        )}
      </div>

      {/* DETAIL PANEL */}
      {selectedHit && (
        <DetailPanel
          hit={selectedHit}
          onClose={() => setSelectedHit(null)}
          onClickChat={handleClickChat}
          onClickQuizz={handleClickQuizz}
          onClickGritAndFit={handleClickGritAndFit}
          actionLoading={actionLoading}
        />
      )}

      <PremiumModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        askedForPremium={user?.asked_for_premium}
        onUpdateUser={updateUser}
        context="Tu as déjà rencontré 2 professionnels"
        reason={premiumModalReason}
      />
    </div>
  );
}
